import React, { useState } from 'react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { Briefcase, Users, TrendingUp, Heart, MapPin, Clock, Send, Upload, Mail, X } from 'lucide-react';
export const CareersPage = () => {
  const [selectedPosition, setSelectedPosition] = useState<string | null>(null);
  const [applicationMethod, setApplicationMethod] = useState<'upload' | 'email' | null>(null);
  const positions = [{
    id: 'social-media',
    title: 'Social Media Executive',
    type: 'Voluntary',
    location: 'Remote',
    description: 'Manage our social media presence across platforms, create engaging content, and grow our online community.',
    responsibilities: ['Create and schedule social media content', 'Engage with followers and respond to comments', 'Monitor social media trends and analytics', 'Develop social media strategies'],
    requirements: ['Experience with social media platforms (Instagram, TikTok, Twitter, LinkedIn)', 'Strong written communication skills', 'Creative mindset and eye for design', 'Understanding of social media analytics']
  }, {
    id: 'admin',
    title: 'Admin Personnel',
    type: 'Voluntary',
    location: 'Nairobi',
    description: 'Support our operations team with administrative tasks, document management, and customer service.',
    responsibilities: ['Handle administrative tasks and documentation', 'Assist with customer inquiries', 'Maintain organized filing systems', 'Support team coordination'],
    requirements: ['Strong organizational skills', 'Proficiency in Microsoft Office', 'Excellent communication skills', 'Attention to detail']
  }];
  const handleApply = (positionId: string) => {
    setSelectedPosition(positionId);
  };
  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      alert(`CV uploaded: ${file.name}\n\nThank you for your application! We'll review your CV and get back to you within 5 business days.`);
      setSelectedPosition(null);
      setApplicationMethod(null);
    }
  };
  const selectedPositionData = positions.find(p => p.id === selectedPosition);
  return <div className="min-h-screen flex flex-col bg-white" data-id="element-1362">
      <Header data-id="element-1363" />

      <main className="flex-grow" data-id="element-1364">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-primary-600 to-primary-700 text-white py-20" data-id="element-1365">
          <div className="container" data-id="element-1366">
            <div className="max-w-4xl mx-auto text-center" data-id="element-1367">
              <div className="w-20 h-20 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-6" data-id="element-1368">
                <Briefcase size={40} data-id="element-1369" />
              </div>
              <h1 className="text-4xl md:text-5xl font-bold mb-6" data-id="element-1370">
                Join the Geldhaus Team
              </h1>
              <p className="text-xl text-primary-100 mb-8" data-id="element-1371">
                Be part of a mission to empower Kenyan businesses. We're looking
                for passionate individuals to join our growing team.
              </p>
              <div className="flex flex-wrap justify-center gap-4" data-id="element-1372">
                <div className="bg-white/10 backdrop-blur-sm rounded-lg px-6 py-3" data-id="element-1373">
                  <p className="text-2xl font-bold" data-id="element-1374">{positions.length}</p>
                  <p className="text-sm text-primary-100" data-id="element-1375">Open Positions</p>
                </div>
                <div className="bg-white/10 backdrop-blur-sm rounded-lg px-6 py-3" data-id="element-1376">
                  <p className="text-2xl font-bold" data-id="element-1377">Remote</p>
                  <p className="text-sm text-primary-100" data-id="element-1378">Work Options</p>
                </div>
                <div className="bg-white/10 backdrop-blur-sm rounded-lg px-6 py-3" data-id="element-1379">
                  <p className="text-2xl font-bold" data-id="element-1380">Growth</p>
                  <p className="text-sm text-primary-100" data-id="element-1381">Opportunities</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Why Join Us */}
        <section className="py-16 bg-gray-50" data-id="element-1382">
          <div className="container" data-id="element-1383">
            <div className="text-center mb-12" data-id="element-1384">
              <h2 className="text-3xl md:text-4xl font-bold mb-4 text-gray-900" data-id="element-1385">
                Why Join Geldhaus?
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto" data-id="element-1386">
                Be part of a team that's making a real difference in Kenyan
                business finance
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto" data-id="element-1387">
              <div className="bg-white p-8 rounded-2xl border-2 border-gray-200" data-id="element-1388">
                <div className="w-14 h-14 bg-primary-100 rounded-xl flex items-center justify-center mb-4" data-id="element-1389">
                  <TrendingUp size={28} className="text-primary-600" data-id="element-1390" />
                </div>
                <h3 className="text-xl font-bold mb-3 text-gray-900" data-id="element-1391">
                  Growth & Learning
                </h3>
                <p className="text-gray-600" data-id="element-1392">
                  Gain valuable experience in fintech and business finance while
                  developing your skills
                </p>
              </div>

              <div className="bg-white p-8 rounded-2xl border-2 border-gray-200" data-id="element-1393">
                <div className="w-14 h-14 bg-primary-100 rounded-xl flex items-center justify-center mb-4" data-id="element-1394">
                  <Users size={28} className="text-primary-600" data-id="element-1395" />
                </div>
                <h3 className="text-xl font-bold mb-3 text-gray-900" data-id="element-1396">
                  Great Team
                </h3>
                <p className="text-gray-600" data-id="element-1397">
                  Work with passionate professionals dedicated to empowering
                  Kenyan entrepreneurs
                </p>
              </div>

              <div className="bg-white p-8 rounded-2xl border-2 border-gray-200" data-id="element-1398">
                <div className="w-14 h-14 bg-primary-100 rounded-xl flex items-center justify-center mb-4" data-id="element-1399">
                  <Heart size={28} className="text-primary-600" data-id="element-1400" />
                </div>
                <h3 className="text-xl font-bold mb-3 text-gray-900" data-id="element-1401">
                  Make an Impact
                </h3>
                <p className="text-gray-600" data-id="element-1402">
                  Help businesses grow and contribute to Kenya's economic
                  development
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Open Positions */}
        <section id="open-positions" className="py-16 bg-white" data-id="element-1403">
          <div className="container" data-id="element-1404">
            <div className="text-center mb-12" data-id="element-1405">
              <h2 className="text-3xl md:text-4xl font-bold mb-4 text-gray-900" data-id="element-1406">
                Open Positions
              </h2>
              <p className="text-xl text-gray-600" data-id="element-1407">
                Voluntary positions with growth opportunities
              </p>
            </div>

            <div className="max-w-4xl mx-auto space-y-6" data-id="element-1408">
              {positions.map((position, index) => <div key={index} className="bg-white border-2 border-gray-200 rounded-2xl p-8 hover:border-primary-300 hover:shadow-xl transition-all duration-300" data-id="element-1409">
                  <div className="flex flex-col md:flex-row md:items-start md:justify-between mb-6" data-id="element-1410">
                    <div data-id="element-1411">
                      <h3 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-1412">
                        {position.title}
                      </h3>
                      <div className="flex flex-wrap gap-3" data-id="element-1413">
                        <span className="inline-flex items-center gap-1 px-3 py-1 bg-primary-100 text-primary-700 rounded-full text-sm font-semibold" data-id="element-1414">
                          <Briefcase size={14} data-id="element-1415" />
                          {position.type}
                        </span>
                        <span className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-semibold" data-id="element-1416">
                          <MapPin size={14} data-id="element-1417" />
                          {position.location}
                        </span>
                      </div>
                    </div>
                  </div>

                  <p className="text-gray-600 mb-6" data-id="element-1418">{position.description}</p>

                  <div className="grid md:grid-cols-2 gap-6 mb-6" data-id="element-1419">
                    <div data-id="element-1420">
                      <h4 className="font-bold text-gray-900 mb-3" data-id="element-1421">
                        Responsibilities
                      </h4>
                      <ul className="space-y-2" data-id="element-1422">
                        {position.responsibilities.map((item, idx) => <li key={idx} className="text-sm text-gray-600 flex items-start gap-2" data-id="element-1423">
                            <span className="text-primary-600 mt-1" data-id="element-1424">•</span>
                            {item}
                          </li>)}
                      </ul>
                    </div>

                    <div data-id="element-1425">
                      <h4 className="font-bold text-gray-900 mb-3" data-id="element-1426">
                        Requirements
                      </h4>
                      <ul className="space-y-2" data-id="element-1427">
                        {position.requirements.map((item, idx) => <li key={idx} className="text-sm text-gray-600 flex items-start gap-2" data-id="element-1428">
                            <span className="text-primary-600 mt-1" data-id="element-1429">•</span>
                            {item}
                          </li>)}
                      </ul>
                    </div>
                  </div>

                  <button onClick={() => handleApply(position.id)} className="w-full md:w-auto bg-primary-600 hover:bg-primary-700 text-white font-bold py-3 px-8 rounded-lg transition-all duration-300 flex items-center justify-center gap-2" data-id="element-1430">
                    <Send size={18} data-id="element-1431" />
                    Apply for This Position
                  </button>
                </div>)}
            </div>
          </div>
        </section>

        {/* Application CTA */}
        <section className="py-16 bg-gradient-to-br from-primary-600 to-primary-700 text-white" data-id="element-1432">
          <div className="container" data-id="element-1433">
            <div className="max-w-3xl mx-auto text-center" data-id="element-1434">
              <h2 className="text-3xl md:text-4xl font-bold mb-6" data-id="element-1435">
                Don't See the Right Position?
              </h2>
              <p className="text-xl text-primary-100 mb-8" data-id="element-1436">
                We're always looking for talented individuals. Send us your CV
                and let us know how you can contribute to Geldhaus.
              </p>
              <a href="mailto:geldhausltd@gmail.com?subject=General Application - Geldhaus Careers" className="inline-block bg-white text-primary-600 hover:bg-primary-50 font-bold py-4 px-8 rounded-xl transition-all duration-300 shadow-lg hover:shadow-xl" data-id="element-1437">
                Send General Application
              </a>
            </div>
          </div>
        </section>
      </main>

      {/* Application Modal */}
      {selectedPosition && <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-fadeIn" data-id="element-1438">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full" data-id="element-1439">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between" data-id="element-1440">
              <h3 className="text-2xl font-bold text-gray-900" data-id="element-1441">
                Apply for Position
              </h3>
              <button onClick={() => {
            setSelectedPosition(null);
            setApplicationMethod(null);
          }} className="text-gray-400 hover:text-gray-600" aria-label="Close application modal" data-id="element-1442">
                <X size={24} data-id="element-1443" />
              </button>
            </div>

            <div className="p-6" data-id="element-1444">
              <p className="text-gray-600 mb-6" data-id="element-1445">
                Applying for: <strong data-id="element-1446">{selectedPositionData?.title}</strong>
              </p>

              {!applicationMethod ? <div className="space-y-4" data-id="element-1447">
                  <p className="text-sm text-gray-600 mb-4" data-id="element-1448">
                    Choose how you'd like to apply:
                  </p>

                  <button onClick={() => setApplicationMethod('upload')} className="w-full flex items-center gap-4 p-4 bg-primary-50 hover:bg-primary-100 border-2 border-primary-200 rounded-xl transition-all duration-300" data-id="element-1449">
                    <div className="w-12 h-12 bg-primary-600 rounded-full flex items-center justify-center" data-id="element-1450">
                      <Upload size={24} className="text-white" data-id="element-1451" />
                    </div>
                    <div className="flex-1 text-left" data-id="element-1452">
                      <p className="font-bold text-gray-900" data-id="element-1453">Upload CV</p>
                      <p className="text-sm text-gray-600" data-id="element-1454">
                        Upload your resume directly
                      </p>
                    </div>
                  </button>

                  <a href={`mailto:geldhausltd@gmail.com?subject=Application for ${selectedPositionData?.title}&body=Dear Hiring Team,%0D%0A%0D%0AI am writing to apply for the ${selectedPositionData?.title} position at Geldhaus.%0D%0A%0D%0APlease find my CV attached.%0D%0A%0D%0ABest regards`} className="w-full flex items-center gap-4 p-4 bg-blue-50 hover:bg-blue-100 border-2 border-blue-200 rounded-xl transition-all duration-300" data-id="element-1455">
                    <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center" data-id="element-1456">
                      <Mail size={24} className="text-white" data-id="element-1457" />
                    </div>
                    <div className="flex-1 text-left" data-id="element-1458">
                      <p className="font-bold text-gray-900" data-id="element-1459">
                        Email Application
                      </p>
                      <p className="text-sm text-gray-600" data-id="element-1460">
                        geldhausltd@gmail.com
                      </p>
                    </div>
                  </a>

                  <a href={`mailto:info@geldhaus.africa?subject=Application for ${selectedPositionData?.title}&body=Dear Hiring Team,%0D%0A%0D%0AI am writing to apply for the ${selectedPositionData?.title} position at Geldhaus.%0D%0A%0D%0APlease find my CV attached.%0D%0A%0D%0ABest regards`} className="w-full flex items-center gap-4 p-4 bg-green-50 hover:bg-green-100 border-2 border-green-200 rounded-xl transition-all duration-300" data-id="element-1461">
                    <div className="w-12 h-12 bg-green-600 rounded-full flex items-center justify-center" data-id="element-1462">
                      <Mail size={24} className="text-white" data-id="element-1463" />
                    </div>
                    <div className="flex-1 text-left" data-id="element-1464">
                      <p className="font-bold text-gray-900" data-id="element-1465">
                        Email Application
                      </p>
                      <p className="text-sm text-gray-600" data-id="element-1466">
                        info@geldhaus.africa
                      </p>
                    </div>
                  </a>
                </div> : <div className="space-y-4" data-id="element-1467">
                  <div className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center" data-id="element-1468">
                    <Upload size={48} className="mx-auto text-gray-400 mb-4" data-id="element-1469" />
                    <p className="text-gray-700 font-semibold mb-2" data-id="element-1470">
                      Upload your CV
                    </p>
                    <p className="text-sm text-gray-500 mb-4" data-id="element-1471">
                      PDF, DOC, or DOCX (Max 5MB)
                    </p>
                    <input type="file" accept=".pdf,.doc,.docx" onChange={handleFileUpload} className="hidden" id="cv-upload" data-id="element-1472" />
                    <label htmlFor="cv-upload" className="inline-block bg-primary-600 hover:bg-primary-700 text-white font-bold py-3 px-6 rounded-lg cursor-pointer transition-all duration-300" data-id="element-1473">
                      Choose File
                    </label>
                  </div>

                  <button onClick={() => setApplicationMethod(null)} className="w-full py-3 text-gray-600 hover:text-gray-800 font-semibold" data-id="element-1474">
                    ← Back to options
                  </button>
                </div>}
            </div>
          </div>
        </div>}

      <Footer data-id="element-1475" />
    </div>;
};